import { useEffect } from 'react';
import { trackEvent } from '@/lib/analytics';

/**
 * containerRef 하위의 [data-ga-section] 엘리먼트들을 관찰해서, 각 섹션이 처음
 * threshold 이상 보였을 때 1회만 GA4 이벤트를 발생시킨다(이후 unobserve).
 * jsdom 등 IntersectionObserver가 없는 환경에서는 조용히 아무 것도 하지 않는다.
 */
export function useSectionViewTracking(
  containerRef: React.RefObject<HTMLElement>,
  eventName: string,
  options?: { threshold?: number }
): void {
  useEffect(() => {
    const container = containerRef.current;
    if (!container || typeof IntersectionObserver === 'undefined') return;

    const targets = container.querySelectorAll<HTMLElement>('[data-ga-section]');
    if (targets.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const section = (entry.target as HTMLElement).dataset.gaSection;
          if (section) {
            trackEvent(eventName, { section });
          }
          observer.unobserve(entry.target);
        });
      },
      { threshold: options?.threshold ?? 0.5 }
    );

    targets.forEach((target) => observer.observe(target));

    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerRef, eventName]);
}
